import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';
import { functions, analytics } from '../../config/firebase';
import { Search, Mail, ArrowRight, Lock, ShieldCheck, AlertCircle, CheckCircle2, Loader2, User } from 'lucide-react';
import BaseBadge from './BaseBadge';

const HOLD_DURATION = 875; // 0.875 seconds

const PaymentBadge = ({ onAuthenticated }) => {
  const [step, setStep] = useState(1); // 1: Customer ID, 2: Email
  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "error" | "success"
  
  const [isPressed, setIsPressed] = useState(false);
  const pressTimerRef = useRef(null);

  // Focus ref for email input
  const emailInputRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    };
  }, []);

  const triggerVerification = useCallback(async () => {
    if (step === 1) {
      if (!customerId.trim() || !customerId.startsWith('cus_')) {
        setMessage("Please enter a valid Customer ID (cus_...).");
        setMessageType("error");
        return;
      }

      setIsLoading(true);
      setMessage("Verifying ID...");
      setMessageType("");

      try {
        const verifyCustomer = httpsCallable(functions, "verifyCustomer");
        const result = await verifyCustomer({ customerId: customerId.trim() });

        if (result.data && result.data.success) {
          setMessage(`Found record for ${result.data.name.split(' ')[0] || 'Customer'}`);
          setMessageType("success");
          setCustomerName(result.data.name);
          setStep(2);
          
          logEvent(analytics, "customer_id_verified", { customer_id: customerId.trim() });
          
          // Auto-focus email after animation
          setTimeout(() => {
            emailInputRef.current?.focus();
            setMessage(""); // clear message to keep UI clean
          }, 800);
        }
      } catch (error) {
        console.error(error);
        setMessage("Customer ID not found.");
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    } else if (step === 2) {
      if (!email.trim() || !email.includes('@')) {
        setMessage("Please enter a valid email address.");
        setMessageType("error");
        return;
      }

      setIsLoading(true);
      setMessage("Authenticating...");
      setMessageType("");

      try {
        const authenticateCustomer = httpsCallable(functions, "authenticateCustomer");
        const result = await authenticateCustomer({ 
          customerId: customerId.trim(), 
          email: email.trim() 
        });

        if (result.data && result.data.success) {
          setMessage("Authentication successful!");
          setMessageType("success");
          
          logEvent(analytics, "customer_authenticated", { customer_id: customerId.trim() });

          // Inform App to navigate to portal
          setTimeout(() => {
            if (onAuthenticated) onAuthenticated(customerId.trim(), customerName);
          }, 600);
        }
      } catch (error) {
        console.error(error);
        setMessage("Email does not match our records.");
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    }
  }, [step, customerId, email, customerName, onAuthenticated]);

  const handlePressStart = useCallback((e) => {
    if (e) e.preventDefault();
    if (isLoading || pressTimerRef.current) return;

    setIsPressed(true);

    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      setIsPressed(false);
      triggerVerification();
    }, HOLD_DURATION);
  }, [isLoading, triggerVerification]);

  const handlePressEnd = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    setIsPressed(false);
  }, []);

  return (
    <BaseBadge colorFrom="#3b82f6" colorTo="#8b5cf6" title="Payments">
      <div className="flex flex-col flex-grow justify-between">

        <div className="space-y-5">
          <h3 className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400 font-semibold text-[17px] tracking-wide flex items-center justify-between">
            <span>Customer Portal</span>
            {step === 2 && (
              <motion.button 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                onClick={() => { setStep(1); setEmail(""); setMessage(""); }}
                className="text-xs text-white/40 hover:text-white/80 transition-colors"
              >
                Change ID
              </motion.button>
            )}
          </h3>

          <div className="flex flex-col gap-4">
            
            {/* Customer ID Input */}
            <motion.div 
              layout
              animate={{ 
                scale: step === 2 ? 0.96 : 1,
                opacity: step === 2 ? 0.6 : 1,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className={`relative transition-all duration-500 rounded-xl ${
                step === 2 ? 'ring-1 ring-emerald-500/50 bg-emerald-500/5' : ''
              }`}
            >
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${step === 2 ? 'text-emerald-400/70' : 'text-white/30 group-focus-within:text-blue-400/70'}`} />
              <input
                type="text"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                placeholder="cus_"
                className={`w-full pl-12 pr-12 py-5 rounded-xl border font-mono text-base tracking-wider transition-all focus:outline-none ${
                  step === 2 
                    ? 'bg-transparent border-transparent text-emerald-100 placeholder-transparent cursor-default' 
                    : 'bg-white/[0.04] border-white/10 text-white placeholder-white/25 focus:ring-2 focus:ring-blue-400/40 focus:border-blue-400/20 focus:bg-white/[0.06]'
                }`}
                disabled={isLoading || step === 2}
              />
              <AnimatePresence>
                {step === 2 && (
                  <motion.div 
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Email Input (Step 2) */}
            <AnimatePresence>
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -20 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -20 }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  className="relative group"
                >
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30 group-focus-within:text-violet-400/70 transition-colors" />
                  <input
                    ref={emailInputRef}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Account Email"
                    className="w-full pl-12 pr-4 py-5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/20 focus:bg-white/[0.06] font-mono text-base tracking-wider transition-all"
                    disabled={isLoading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Optional: Enter triggers lookup without holding
                      }
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-4">
              <motion.button
                layout
                type="button"
                onPointerDown={handlePressStart}
                onPointerUp={handlePressEnd}
                onPointerLeave={handlePressEnd}
                onPointerCancel={handlePressEnd}
                disabled={isLoading}
                animate={{
                  borderColor: isPressed ? "rgba(59, 130, 246, 0.5)" : "rgba(255, 255, 255, 0.1)",
                  boxShadow: isPressed 
                    ? "0 0 20px rgba(59, 130, 246, 0.3), inset 0 0 12px rgba(59, 130, 246, 0.1)" 
                    : "0 0 0px rgba(59, 130, 246, 0), inset 0 0 0px rgba(59, 130, 246, 0)"
                }}
                className="relative w-full px-6 py-4 rounded-xl overflow-hidden bg-white/[0.02] border backdrop-blur-sm font-semibold flex justify-center items-center gap-2.5 text-white disabled:opacity-50 select-none cursor-pointer active:scale-[0.98] transition-all duration-300 mt-2"
              >
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: isPressed ? 1 : 0 }}
                  style={{ originX: 0 }}
                  transition={{ 
                    duration: isPressed ? 0.875 : 0.4, 
                    ease: isPressed ? [0.22, 1, 0.36, 1] : "easeOut" 
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-blue-600/40 via-cyan-400/50 to-violet-500/40 pointer-events-none z-0 blur-[2px]"
                />

                <span className="relative z-10 flex items-center gap-2.5">
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white/50" />
                  ) : (
                    <>
                      <span className="tracking-wide text-white/50">
                        {isPressed 
                          ? 'Hold to confirm...' 
                          : step === 1 
                            ? 'Verify ID' 
                            : 'Access Portal'}
                      </span>
                      {!isPressed && <ArrowRight className="w-4 h-4 text-white/50" />}
                    </>
                  )}
                </span>
              </motion.button>
            </div>
          </div>
        </div>

        <div className="h-14 flex items-end">
          <AnimatePresence mode="wait">
            {message && (
              <motion.div
                key={message}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className={`w-full p-3 rounded-lg text-xs flex items-center gap-2 ${
                  messageType === "error"
                    ? "bg-red-500/10 text-red-300 border border-red-500/20"
                    : messageType === "success"
                    ? "bg-green-500/10 text-green-300 border border-green-500/20"
                    : "bg-white/5 text-white/70 border border-white/10"
                }`}
              >
                {messageType === "error" && <AlertCircle className="w-4 h-4 shrink-0" />}
                {messageType === "success" && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                <span className="truncate">{message}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-2.5 mt-2">
          <div className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/[0.05] border border-white/10">
            <Lock className="w-4 h-4 text-white/50" />
            <span className="text-[11px] text-white/50 font-medium tracking-wide">SSL</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/[0.05] border border-white/10">
            <ShieldCheck className="w-4 h-4 text-white/50" />
            <span className="text-[11px] text-white/50 font-medium tracking-wide">PCI</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/[0.05] border border-white/10">
            <img 
              src="https://www.vectorlogo.zone/logos/stripe/stripe-ar21.svg" 
              alt="Stripe" 
              className="h-[18.5px] w-auto object-contain brightness-0 invert opacity-60"
            />
          </div>
        </div>
      </div>
    </BaseBadge>
  );
};

export default PaymentBadge;
